/**
 * TileSwap Animation
 * Creates a cascading tile animation effect by sampling colors from images
 * and animating tiles based on their darkness levels.
 */

// Configuration constants
const CONFIG = {
  GRID_SIZE: 48,
  ANIMATION_DELAY: 0,
  FRAME_DELAY: 20,
  CANVAS_SIZE: "50%",
  ROUNDS_PER_COMPLETION: 24,
  IMAGE_SOURCES: [
    "/imgs/1.png",
    "/imgs/2.png",
    "/imgs/3.png",
    "/imgs/4.png",
    "/imgs/5.png",
    "/imgs/6.png",
    "/imgs/7.png",
    "/imgs/8.png",
    "/imgs/9.png",
    "/imgs/10.png",
  ],
  // IMAGE_SOURCES: ["red.png", "blue.png", "green.png", "yellow.png"],
};

// Global state
const state = {
  images: [],
  currentImageIndex: 0,
  imagIndexDirection: 1,
  tileData: [],
  isAnimating: false,
};

/**
 * Loads all images and initializes the application when complete
 */
function loadImages() {
  let loadedCount = 0;
  const totalImages = CONFIG.IMAGE_SOURCES.length;

  CONFIG.IMAGE_SOURCES.forEach((src, index) => {
    const image = new Image();

    image.onload = () => {
      loadedCount++;
      if (loadedCount === totalImages) {
        init();
      }
    };

    image.onerror = () => {
      console.error(`Failed to load image: ${src}`);
      loadedCount++;
      if (loadedCount === totalImages) {
        init();
      }
    };

    image.src = src;
    state.images.push(image);
  });
}

/**
 * Initializes the canvas and tile system
 */
function init() {
  const canvas = createCanvas();
  const ctx = canvas.getContext("2d");

  initializeTileData();
  setTileColors();

  // Start the animation loop
  render(ctx);
}

/**
 * Creates and configures the main canvas
 */
function createCanvas() {
  const canvas = document.getElementById("jacob");
  const ctx = canvas.getContext("2d");

  // Set canvas dimensions
  canvas.width = CONFIG.GRID_SIZE;
  canvas.height = CONFIG.GRID_SIZE;

  // Set display size and rendering properties
  canvas.style.width = CONFIG.CANVAS_SIZE;
  canvas.style.height = CONFIG.CANVAS_SIZE;
  canvas.style.imageRendering = "pixelated";

  // Disable image smoothing for pixel-perfect rendering
  ctx.imageSmoothingEnabled = false;
  ctx.webkitImageSmoothingEnabled = false;
  ctx.mozImageSmoothingEnabled = false;
  ctx.msImageSmoothingEnabled = false;
  ctx.oImageSmoothingEnabled = false;
  return canvas;
}

/**
 * Initializes tile data structure with positions
 */
function initializeTileData() {
  const tileCount = CONFIG.GRID_SIZE * CONFIG.GRID_SIZE;

  for (let i = 0; i < tileCount; i++) {
    const x = i % CONFIG.GRID_SIZE;
    const y = Math.floor(i / CONFIG.GRID_SIZE);

    state.tileData.push({
      x,
      y,
      color: null,
      isUpdated: false,
    });
  }
}

/**
 * Samples colors from the current image for each tile
 */
function setTileColors() {
  const image = state.images[state.currentImageIndex];
  const tempCanvas = document.createElement("canvas");
  const tempCtx = tempCanvas.getContext("2d");

  tempCanvas.width = image.width;
  tempCanvas.height = image.height;
  tempCtx.drawImage(image, 0, 0);

  const imageData = tempCtx.getImageData(0, 0, image.width, image.height);
  const data = imageData.data;

  state.tileData.forEach((tile) => {
    const index = (tile.y * image.width + tile.x) * 4;
    const r = data[index];
    const g = data[index + 1];
    const b = data[index + 2];

    // Calculate lightness using luminance formula
    const lightness = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

    // Create pixel data for this tile
    const pixelData = new ImageData(
      new Uint8ClampedArray([r, g, b, 255]),
      1,
      1
    );
    tile.isUpdated = false;
    tile.color = {
      r,
      g,
      b,
      lightness: lightness * 100,
      pixelData,
    };
  });

  // Cycle to next image
  // state.currentImageIndex = (state.currentImageIndex + 1) % state.images.length;
  state.currentImageIndex = state.currentImageIndex + state.imagIndexDirection;
  if (state.currentImageIndex > state.images.length - 1) {
    state.imagIndexDirection = -1;
    state.currentImageIndex = state.images.length - 2;
  } else if (state.currentImageIndex < 0) {
    state.imagIndexDirection = 1;
    state.currentImageIndex = 1;
  }
}

/**
 * Sorts tiles by darkness (lightest to darkest)
 */
function sortTilesByDarkness() {
  state.tileData.sort((a, b) => a.color.lightness - b.color.lightness);
}

/**
 * Draws all tiles at once (for initial render)
 */
function drawAllTiles(ctx) {
  state.tileData.forEach((tile) => {
    ctx.putImageData(tile.color.pixelData, tile.x, tile.y);
    tile.isUpdated = true;
  });
}

/**
 * Animates tiles one by one in sorted order
 */
function render(ctx) {
  if (state.isAnimating) return;

  state.isAnimating = true;

  // Initial setup
  drawAllTiles(ctx);
  sortTilesByDarkness();
  setTileColors();

  let colIndex = 0;
  const totalCols = CONFIG.GRID_SIZE + CONFIG.ROUNDS_PER_COMPLETION;

  function animateNextTile() {
    if (colIndex >= totalCols) {
      // Animation complete, prepare for next cycle
      state.isAnimating = false;
      setTimeout(() => {
        render(ctx);
      }, CONFIG.ANIMATION_DELAY);
      return;
    }

    const startCol = Math.max(0, colIndex - CONFIG.ROUNDS_PER_COMPLETION);
    const endCol = Math.min(colIndex, CONFIG.GRID_SIZE - 1);

    for (let col = startCol; col <= endCol; col++) {
      // grab the percentage of tiles in this column that need to be updated
      const tilesAvailableToUpdate = state.tileData.filter(
        (tile) => tile.x === col && !tile.isUpdated
      );
      const tilesToUpdate = Math.min(
        tilesAvailableToUpdate.length,
        Math.ceil(CONFIG.GRID_SIZE / CONFIG.ROUNDS_PER_COMPLETION)
      );
      tilesAvailableToUpdate.sort(
        (a, b) => a.color.lightness - b.color.lightness
      );
      // tilesAvailableToUpdate.sort(() => 0.5 - Math.random());
      // update the tiles in this column
      for (let i = 0; i < tilesToUpdate; i++) {
        const tile = tilesAvailableToUpdate[i];
        tile.isUpdated = true;
        ctx.putImageData(tile.color.pixelData, tile.x, tile.y);
      }
    }
    colIndex++;
    setTimeout(animateNextTile, CONFIG.FRAME_DELAY);
  }

  animateNextTile();
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", loadImages);
